/*
 *     Copyright (c) 2020 Bcame S.r.l.. All rights reserved.
 *     Bcame S.r.l. Technology Business Unit
 *
 *     Company confidential.
 *
 *     This software is proprietary  to and embodies the confidential
 *     technology of Bcame S.r.l..  Possession, use, or copying of this
 *     software and media is authorized only pursuant to a valid written
 *     license from Bcame S.r.l.  or an authorized sublicensor.
 *
 *     Bcame S.r.l.
 */

import { DefaultShipping, DefaultBilling } from '@/store/modules/checkout/defaultValues'
import { EventBus, graphqlManageError } from '@/helpers'
import Config from 'config'
import Graphql from '@/services/Graphql'
import HttpProvider from '@/services/HttpProvider'
import Logger from '@/services/Logger'
import { manageAttributesLabel } from '@/helpers/utils'
import { clearAllCache } from '@/store/lib/search'
import customerAddressCount from '@/services/graphQlquery/customerAddressCount'
import find from 'lodash/find'

function refreshProductOnWishlist (customer) {
  if (customer.wishlist && customer.wishlist.items.length) {
    customer.wishlist.items.forEach((item, index) => {
      let product = item.product
      product = { ...item.product, ...product.configurableParent }
      // find attributes
      product.presetFilter = {}
      item.selectedOptions.forEach(option => {
        product.presetFilter[option.option_id] = option.option_value
      })
      product.groupedVariation.map((groupedItem) => {
        groupedItem.attributesLabel = manageAttributesLabel(groupedItem.attributesLabel)
      })
      item.product = product
    })
  }
  return customer
}

export default {
  /**
   * Login user and return user profile and current token
   */
  afterLogin (ctx, token) {
    ctx.commit('setToken', token)
    ctx.dispatch('emptyCheckout')
    // clean cached call
    clearAllCache()
    return ctx.dispatch('me', { token, invalid: true, fromLogin: true })
      .then(() => {
        return ctx.dispatch('moveCartFromGuest')
      })
      .then(() => {
        return ctx.dispatch('moveWishlistFromGuest').finally(() => {
          return ctx.commit('setGuest', null)
        })
      })
      .then(() => {
        EventBus.$emit('user:isLoggedIn')
        return ctx.state.current
      })
      .catch(err => {
        Logger.error(err)
        throw err
      })
  },

  login (ctx, { username, password }) {
    return HttpProvider.post(
      Config.API.users.login.replace(
        '{{storeViewCode}}', ctx.rootState.ui.storeViewCode
      ),
      { username, password },
      {
        headers: {
          'Authorization': `Bearer ${process.env.VUE_APP_MAGENTO_TOKEN}`
        }
      })
      .then((resp) => {
        if (resp.status === 200) {
          return ctx.dispatch('afterLogin', resp.data)
        }
        return resp.data
      })
  },

  me (context, { token, invalid = false, fromLogin, notRedirectLogoutAfterException = false }) {
    if (context.state.current && !invalid) {
      return Promise.resolve(context.state.current)
    } else {
      // config, token, storeViewCode, graphSchemaType, variables = null, useCache = true
      return Graphql.genericCall(
        {},
        token,
        context.rootState.ui.storeViewCode,
        'graphCustomer',
        null,
        false)
        .then(resp => {
          if (resp && resp.data && resp.data.customer) {
            let customer = resp.data.customer

            // if has default shipping use it
            let defaultShipping = { ...DefaultShipping }
            let defaultBilling = { ...DefaultBilling }
            context.dispatch('presetAddressCheckout', { mode: 'shipping', mutationName: 'setShippingAddress', defaultAddress: defaultShipping, customer })
            context.dispatch('presetAddressCheckout', { mode: 'billing', mutationName: 'setBillingAddress', defaultAddress: defaultBilling, customer })
            // refresh product on wishlist
            refreshProductOnWishlist(customer)
            context.commit('setCustomer', customer)
            EventBus.$emit('user:update')

            // if logged without cartid
            if (!fromLogin && context.rootState.cart.cartId === null) {
              context.dispatch('cart/syncServerCart', {}, { root: true })
            }
            return customer
          } else {
            if (!notRedirectLogoutAfterException) {
              context.dispatch('logout')
            }
            throw new Error('session expired')
          }
        })
        .catch(err => {
          if (!notRedirectLogoutAfterException) {
            context.dispatch('logout')
          }
          throw err
        })
    }
  },
  /**
   * Logout user
   */
  logout (context) {
    context.commit('setCustomer', null)
    context.commit('setToken', null)
    context.commit('newUser', false)
    context.dispatch('cart/empty', null, { root: true })
    context.dispatch('emptyCheckout')
    EventBus.$emit('user:loggedOut', true)
    // clean cached call
    clearAllCache()
    global.$router.replace({ name: 'home' })
    // location.href = global.$router.resolve({ name: 'home' }).href
  },
  /**
   * Register user and return user profile and current token
   */
  create (context, data) {
    var params = {
      password: data.password,
      customer: {
        prefix: data.prefix,
        gender: data.gender,
        dob: data.dob,
        email: data.email,
        firstname: data.firstname,
        lastname: data.lastname,
        store_id: context.rootGetters['storeConfig/storeviewID'],
        extension_attributes: {
          is_subscribed: data.privacy.terms1,
          customer_ip: context.state.ip,
          profiling: data.privacy.terms2
        }
      }
    }
    return HttpProvider.post(
      Config.API.users.create.replace('{{storeViewCode}}', context.rootState.ui.storeViewCode),
      params,
      {
        headers: {
          'Authorization': `Bearer ${process.env.VUE_APP_MAGENTO_TOKEN}`
        }
      })
      .then((resp) => {
        if (resp.status === 200) {
          return context.dispatch('login', { username: data.email, password: data.password })
        }
        throw new Error('user not found')
      })
      .catch(err => {
        throw err
      })
  },
  /**
   * Update user data
   */
  update (context, data) {
    // clean for update
    delete data.default_shipping
    delete data.default_billing
    delete data.addresses
    delete data.wishlist
    delete data.id
    let token = context.state.token
    return Graphql.genericCall(
      {},
      token,
      context.rootState.ui.storeViewCode,
      'updateCustomer',
      {
        customerInput: data
      },
      false)
      .then(resp => {
        if (resp.data && resp.data.updateCustomer && resp.data.updateCustomer) {
          let customer = resp.data.updateCustomer.customer
          refreshProductOnWishlist(customer)
          context.commit('setCustomer', customer)
          return resp.data.updateCustomer.customer
        } else {
          throw new Error(resp)
        }
      })
      .catch(err => {
        Logger.error(err)
        throw new Error('GenericError')
      })
  },
  /**
   * change password
   */
  changePass (ctx, data) {
    return Graphql.genericCall(
      {},
      ctx.state.token,
      ctx.rootState.ui.storeViewCode,
      'customerChangePassword',
      data,
      false)
      .then(res => {
        if (graphqlManageError(res)) {
          // eslint-disable-next-line
          throw { message: graphqlManageError(res) }
        }
      })
  },

  updateAddressList (ctx, { address, index }) {
    let params = { ...ctx.state.current, addresses: [ ...ctx.state.current.addresses ] }
    if (index !== null) {
      params.addresses[index] = address
    } else {
      params.addresses.push(address)
    }
    // to do remove after change backend -- fix region
    if (address.region && address.region.region_code) {
      const region = ctx.rootGetters['checkout/getRegionByCode']({ regionCode: address.region.region_code, countryID: address.country_id })
      address.region = {
        region: region.name,
        region_id: Number(region.id),
        region_code: region.code
      }
      delete address.region_id
    }
    let id = address.id
    delete address.id

    let token = ctx.state.token
    return Graphql.genericCall(
      {},
      token,
      ctx.rootState.ui.storeViewCode,
      id ? 'updateAddress' : 'createAddress',
      {
        id: id,
        AddressInput: address
      },
      false)
      .then(resp => {
        let customerAddress = resp && resp.data ? resp.data.updateCustomerAddress || resp.data.createCustomerAddress : null
        if (customerAddress) {
          ctx.commit('setAddress', customerAddress)
          return ctx.state.current
        } else {
          throw new Error(resp)
        }
      })
      .catch(err => {
        Logger.error(err)
        throw new Error('GenericError')
      })
    // return ctx.dispatch('update', params)
  },

  deleteAddress (ctx, id) {
    // var params = {}
    /* assign(params, ctx.state.current)
    if (index !== null) {
      params.addresses.splice(index, 1)
    } */
    let token = ctx.state.token
    return Graphql.genericCall(
      {},
      token,
      ctx.rootState.ui.storeViewCode,
      'deleteAddress',
      {
        id
      },
      false)
      .then(resp => {
        if (graphqlManageError(resp)) {
          // eslint-disable-next-line
          throw { body: { result: graphqlManageError(resp) } }
        }
        if (resp && resp.data && resp.data.deleteCustomerAddress) {
          ctx.commit('deleteAddress', id)
          return ctx.state.current
        }
      })
  },

  getUserGuest (ctx) {
    if (ctx.state.guest.email) {
      return Promise.resolve(ctx.state.guest)
    } else {
      const userStore = require('@/store/lib/storage').userStore
      return userStore.getItem('guest').then(guest => {
        ctx.commit('setGuest', guest)
        return ctx.state.guest
      })
    }
  },
  // remove checkout last information from cache
  emptyCheckout (ctx) {
    ctx.dispatch('checkout/empty', null, { root: true })
  },

  presetAddressCheckout (ctx, { mode, mutationName, defaultAddress, customer }) {
    // preset address checkout if not setted
    if (ctx.rootState.checkout[`${mode}Address`].firstname === '') {
      const addressID = customer[`default_${mode}`]
      if (addressID) {
        const Address = find(customer.addresses, { id: Number(addressID) })
        ctx.commit(`checkout/${mutationName}`, { ...defaultAddress, ...Address }, { root: true })
      }
    }
  },

  moveCartFromGuest (ctx) {
    if (ctx.rootGetters['cart/isEmptyCart']) {
      return ctx.dispatch('cart/syncServerCart', { invalid: true }, { root: true })
    }
    // get guest cartid
    return ctx.dispatch('cart/mergeCart', { guestCartID: ctx.rootState.cart.cartId }, { root: true })
      .then(() => {
        return ctx.dispatch('cart/syncServerCart', { invalid: true }, { root: true })
      })
      .catch(() => {
        return ctx.dispatch('cart/syncServerCart', { invalid: true }, { root: true })
      })
  },
  moveAllToCart (ctx) {
    return Graphql.genericCall(
      {},
      ctx.rootState.user.token,
      ctx.rootState.ui.storeViewCode,
      'moveAllToCart',
      null,
      false
    )
  },
  forgotpassword (ctx, { email }) {
    return HttpProvider.put(
      Config.API.users.resetPassword
        .replace('{{storeViewCode}}', ctx.rootState.ui.storeViewCode),
      {
        email,
        template: 'email_reset',
        websiteId: ctx.rootState.storeConfig.config.website_id
      }
    )
      .then(res => res.data)
  },
  resetPassword (ctx, { email, resetToken, newPassword }) {
    return HttpProvider.post(
      Config.API.users.resetPasswordFinalStep
        .replace('{{storeViewCode}}', ctx.rootState.ui.storeViewCode),
      {
        email,
        resetToken,
        newPassword
      }
    )
      .then(res => res.data)
  },
  /**
   * reorder:  populate basket with selected order
   */
  reorder (ctx, { id }) {
    return HttpProvider.get(
      Config.API.order.reorder
        .replace('{{storeViewCode}}', ctx.rootState.ui.storeViewCode)
        .replace('{{orderId}}', id))
      .then(resp => {
        if (resp.status === 200 && resp.data === 'ok') {
          return ctx.dispatch('cart/syncServerCart', { invalid: true }, { root: true })
        }
        throw new Error(resp)
      })
  },
  unsubscribe (ctx, { email }) {
    return Graphql.genericCall(
      {},
      process.env.VUE_APP_MAGENTO_TOKEN,
      ctx.rootState.ui.storeViewCode,
      'unsubscribeEmail',
      {
        customerInput: {
          email,
          profiling: false
        }
      },
      false)
      .then(resp => {
        if (graphqlManageError(resp)) {
          // eslint-disable-next-line
          throw { body: { result: graphqlManageError(resp) } }
        }
        if (resp.data) {
          let updateCustomer = { ...ctx.state.current, profiling: resp.data.Bcame_set_profiling }
          ctx.commit('setCustomer', updateCustomer)
          return true
        } else {
          throw new Error(resp)
        }
      })
      .catch(err => {
        Logger.error(err)
        throw new Error('GenericError')
      })
  },
  emailAvailable (ctx, email) {
    return HttpProvider.post(
      Config.API.users.emailAvailable.replace(
        '{{storeViewCode}}', ctx.rootState.ui.storeViewCode
      ),
      { customerEmail: email },
      {
        headers: {
          'Authorization': `Bearer ${process.env.VUE_APP_MAGENTO_TOKEN}`
        }
      })
      .then(res => {
        console.log('res', res)
      })
  },
  init (ctx) {
    if (ctx.state.token) {
      if (process.client) {
        const userStore = require('@/store/lib/storage').userStore
        return userStore.getItem('newUser').then(newUser => ctx.commit('newUser', newUser || false))
      }
      return ctx.dispatch('me', { token: ctx.state.token, invalid: true }).then(res => {
        EventBus.$emit('wishlist:update')
      })
    } else {
      ctx.commit('newUser', false)
      ctx.commit('setCustomer', null)
      const guestWishlistShareCode = require('@/store/lib/storage').guestWishlistShareCode
      guestWishlistShareCode.getItem('guestWishlist').then(res => {
        if (res) {
          ctx.commit('guestSetWishlistShareCode', res)
          return ctx.dispatch('getWishlistByShareCode', { sharingCode: res }).then(items => {
            return ctx.commit('guestSetWishlistItem', items || [])
          })
        }
      }).finally(() => {
        EventBus.$emit('wishlist:update')
      })
    }
  },
  getWishlistByShareCode (ctx, { sharingCode, forShare = false }) {
    let variables = {
      sharing_code: sharingCode
    }
    return Graphql.genericCall(
      {},
      ctx.rootState.user.token,
      ctx.rootState.ui.storeViewCode,
      'getWishlistBySharingCode',
      variables,
      false
    )
      .catch(err => {
        console.error(err)
        throw err
      })
      .then((res) => {
        if (graphqlManageError(res)) {
          // eslint-disable-next-line
          throw { body: { result: graphqlManageError(res) } }
        }
        res.data.wishlist.items.map((item) => {
          item.product.shared = forShare
          let configurableOptions = item.product.configurable_options
          const colorAttr = find(configurableOptions, { 'attribute_code': 'color' })
          const sizeAttr = find(configurableOptions, { 'attribute_code': 'size' })
          let color = find(item.selectedOptions, function (test) {
            return test.option_id.toString() === colorAttr.attribute_id
          })
          item.product.presetFilter = {
            [colorAttr.attribute_id]: color.option_value
          }
          let size = find(item.selectedOptions, function (test) {
            return test.option_id.toString() === sizeAttr.attribute_id
          })
          if (size) {
            item.product.presetFilter[sizeAttr.attribute_id] = size.option_value
          }
          item.product.groupedVariation.map((groupedItem) => {
            groupedItem.attributesLabel = manageAttributesLabel(groupedItem.attributesLabel)
          })
        })
        return res.data.wishlist.items
      })
  },
  shareWishlist (ctx) {
    let variables = {
      share: {
        emails: ['francesco.coniglione@bcame.it']
      }
    }
    return Graphql.genericCall(
      {},
      ctx.rootState.user.token,
      ctx.rootState.ui.storeViewCode,
      'shareWishlist',
      variables,
      false
    )
      .catch(err => {
        console.error(err)
        throw err
      })
      .then((res) => {
        if (graphqlManageError(res)) {
          // eslint-disable-next-line
          throw { body: { result: graphqlManageError(res) } }
        }
        let basePathLang = ctx.rootGetters['storeConfig/basePathLang']
        let urlReturn = basePathLang + 'wishlist/shared/'
        if (ctx.state.current && ctx.state.current.wishlist) {
          urlReturn = urlReturn.concat(ctx.state.current.wishlist.sharing_code)
        } else {
          urlReturn = urlReturn.concat(ctx.state.guest.wishlist.sharing_code)
        }
        return urlReturn
      })
  },
  moveWishlistFromGuest (ctx) {
    const guestWishlist = ctx.state.guest.wishlist.items
    guestWishlist.forEach(item => {
      let isOnWishlist = ctx.getters.isOnWishlist(item.product)
      if (!isOnWishlist) {
        // check item.product.filterActive before item.product.presetFilter
        ctx.dispatch('addToWishlist', { product: item.product, filters: item.product.filterActive || item.product.presetFilter })
      }
    })
    ctx.commit('guestSetWishlistShareCode', null)
    return true
  },
  getCustomerAddressCount (ctx) {
    // check if user has address already setted
    // config, token, storeViewCode, graphSchemaType, variables = null, useCache = true, option = null
    if (ctx.state.current && ctx.state.current.addresses) {
      return Promise.resolve(ctx.state.current.addresses.length)
    } else {
      return Graphql.genericCall(
        {},
        ctx.state.token,
        ctx.rootState.ui.storeViewCode,
        '',
        null,
        false,
        {
          query: customerAddressCount
        })
        .then((res) => {
          if (res && res.data && res.data.customer) {
            return res.data.customer.addresses.length
          } else {
            return false
          }
        })
    }
  }
}
